import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import { postsApi, pollsApi } from '../../services/api';
import ComposerPreviewModal from '../ComposerPreviewModal/ComposerPreviewModal';
import PostCard from '../PostCard/PostCard';
import RichEditor from '../RichEditor';
import { htmlToMarkdown, shortcodesToMarkdown, uploadImage, countContentCharacters } from '../RichEditor/richEditorUtils';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import { DEFAULT_FEED_KEY, FEED_DISPLAY_CONFIG, getDefaultEnabledFeedKeys, getFeedCharacterLimit } from '../../config/feed-details';
import './CreatePostModal.css';

const CreatePostModal = ({ onClose }) => {
  const { user } = useAuth();
  const { signature, signatureOnShortPosts } = useSettings();
  const location = useLocation();
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [selectedFeed, setSelectedFeed] = useState(DEFAULT_FEED_KEY);
  const [enabledFeedKeys, setEnabledFeedKeys] = useState([]);
  const [posting, setPosting] = useState(false);
  const [postingError, setPostingError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isPoll, setIsPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollChoices, setPollChoices] = useState(['', '']);
  const [pollDurationValue, setPollDurationValue] = useState(1);
  const [pollDurationUnit, setPollDurationUnit] = useState('days'); // 'days' | 'hours' | 'minutes'
  const richEditorRef = useRef(null);

  useBodyScrollLock(true);

  const fallbackEnabledFeedKeys = useMemo(() => getDefaultEnabledFeedKeys(), []);

  useEffect(() => {
    const fetchEnabledFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        if (!Array.isArray(feeds)) {
          throw new Error('Invalid feeds-config response');
        }
        const allowed = feeds
          .filter((feed) => feed?.enabled !== false)
          .map((feed) => String(feed.key || '').toLowerCase())
          .filter((key) => key && key !== 'global' && FEED_DISPLAY_CONFIG[key]);

        setEnabledFeedKeys(allowed.length ? allowed : fallbackEnabledFeedKeys);
      } catch (error) {
        console.error('Error fetching feeds config:', error);
        setEnabledFeedKeys(fallbackEnabledFeedKeys);
      }
    };

    fetchEnabledFeeds();
  }, [fallbackEnabledFeedKeys]);

  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    let resolved = DEFAULT_FEED_KEY;

    if (parts[0] === 'feed' && parts[1]) {
      resolved = parts[1].toLowerCase();
    } else if (parts[0] === 'user' && parts[2] === 'feed' && parts[3]) {
      resolved = parts[3].toLowerCase();
    }

    const fallback = enabledFeedKeys.includes(DEFAULT_FEED_KEY)
      ? DEFAULT_FEED_KEY
      : (enabledFeedKeys[0] || DEFAULT_FEED_KEY);
    setSelectedFeed(enabledFeedKeys.includes(resolved) ? resolved : fallback);
  }, [location.pathname, enabledFeedKeys]);

  const handleBodyChange = (html) => {
    setBodyHtml(html);
    const outputFormat = (selectedFeed === 'flex' || selectedFeed === 'flexus') ? 'shortcode' : 'markdown';
    const markdown = htmlToMarkdown(html, outputFormat);
    setBody(markdown);
  };

  const handleUploadImage = useCallback(async (file) => {
    if (!user?.username) throw new Error('Login required to upload images');
    return uploadImage(file, user.username, hiveService);
  }, [user]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    setPostingError('');

    if (!user) {
      setPostingError('Please login to post');
      return;
    }

    const outputFormat = (selectedFeed === 'flex' || selectedFeed === 'flexus') ? 'shortcode' : 'markdown';
    const normalizedBody = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml, outputFormat) : body;
    if (!normalizedBody.trim()) {
      setPostingError('Post body cannot be empty');
      return;
    }

    const charLimit = getFeedCharacterLimit(selectedFeed);
    if (countContentCharacters(normalizedBody) > charLimit) {
      setPostingError(`Post exceeds ${charLimit} character limit for ${selectedFeed}`);
      return;
    }

    const bodyTags = Array.from(new Set((normalizedBody.match(/#[a-z0-9-]+/gi) || []).map((h) => h.slice(1).toLowerCase())));

    let pollMetadata = null;
    let pollEndTimeIso = null;

    if (isPoll) {
      const trimmedQuestion = pollQuestion.trim();
      const validChoices = pollChoices.map(c => c.trim()).filter(Boolean);

      if (!trimmedQuestion) {
        setPostingError('Please enter a poll question');
        return;
      }
      if (validChoices.length < 2) {
        setPostingError('Please provide at least 2 poll choices');
        return;
      }

      const numVal = Math.max(1, Number(pollDurationValue) || 1);
      // Maximum 7 days in minutes: 7 * 24 * 60 = 10080 minutes
      const maxMinutes = 7 * 24 * 60;
      let durationMinutes = numVal;
      if (pollDurationUnit === 'days') {
        durationMinutes = numVal * 24 * 60;
      } else if (pollDurationUnit === 'hours') {
        durationMinutes = numVal * 60;
      }
      if (durationMinutes > maxMinutes) {
        durationMinutes = maxMinutes;
      }

      const endTimestampSec = Math.floor(Date.now() / 1000) + (durationMinutes * 60);
      pollEndTimeIso = new Date(endTimestampSec * 1000).toISOString();

      pollMetadata = {
        content_type: 'poll',
        version: 0.8,
        question: trimmedQuestion,
        choices: validChoices,
        preferred_interpretation: 'number_of_votes',
        end_time: endTimestampSec,
        allow_vote_changes: true,
        filters: { account_age: 0 },
        ui_hide_res_until_voted: false
      };
    }

    setPosting(true);
    const feedType = selectedFeed || DEFAULT_FEED_KEY;
    const permlink = feedType === 'threads'
      ? hiveService.generateThreadsPermlink('leothreads')
      : hiveService.generatePermlink();

    try {
      const bodyWithSignature = hiveService.appendUserSignatureForContext(
        normalizedBody,
        user.username,
        'short',
        signature,
        signatureOnShortPosts
      );

      const jsonMetadataPayload = {
        app: 'flexus/1.0.0',
        tags: bodyTags,
        ...(pollMetadata || {})
      };

      await postsApi.createDraft({
        type: 'short',
        author: user.username,
        permlink,
        body: bodyWithSignature,
        json_metadata: jsonMetadataPayload
      });

      await hiveService.createPost(user.username, shortcodesToMarkdown(bodyWithSignature), bodyTags, feedType, {
        permlink,
        signature,
        applySignature: signatureOnShortPosts,
        jsonMetadata: jsonMetadataPayload
      });

      // If poll, save definition into local database immediately
      if (pollMetadata) {
        try {
          await pollsApi.upsertPoll(user.username, permlink, {
            question: pollMetadata.question,
            choices: pollMetadata.choices,
            preferred_interpretation: pollMetadata.preferred_interpretation,
            end_time: pollEndTimeIso,
            allow_vote_changes: pollMetadata.allow_vote_changes,
            hide_res_until_voted: pollMetadata.ui_hide_res_until_voted,
            filters: pollMetadata.filters
          });
        } catch (pollDbErr) {
          console.warn('Failed to register poll in local database:', pollDbErr);
        }
      }

      if (user.username) {
        await postsApi.updateStatus({ type: 'short', author: user.username, permlink, is_pending: false });
      }

      setBody('');
      setBodyHtml('');
      setIsPoll(false);
      setPollQuestion('');
      setPollChoices(['', '']);
      setPreviewOpen(false);
      onClose();
      alert('Post created successfully!');
    } catch (err) {
      console.error('Error creating post:', err);
      const message = err?.message || 'Failed to create post. Please try again.';
      setPostingError(message);

      if (user?.username) {
        await postsApi.updateStatus({ type: 'short', author: user.username, permlink, is_pending: true, pending_reason: 'error' }).catch(() => {});
      }
    } finally {
      setPosting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal-backdrop') {
      onClose();
    }
  };

  const getPreviewParentAuthor = () => {
    return FEED_DISPLAY_CONFIG[selectedFeed]?.root_author || FEED_DISPLAY_CONFIG[DEFAULT_FEED_KEY]?.root_author || '';
  };

  const outputFormat = (selectedFeed === 'flex' || selectedFeed === 'flexus') ? 'shortcode' : 'markdown';
  const previewContentRaw = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml, outputFormat) : body;
  const isPreviewFlex = selectedFeed === 'flex' || selectedFeed === 'flexus';
  const previewBroadcastBody = isPreviewFlex ? previewContentRaw : shortcodesToMarkdown(previewContentRaw);
  const previewContent = user
    ? hiveService.appendUserSignatureForContext(previewBroadcastBody, user.username, 'short', signature, signatureOnShortPosts)
    : previewBroadcastBody;
  const previewPost = user ? {
    author: user.username,
    permlink: 'preview-short-modal',
    body: previewContent,
    created: new Date().toISOString(),
    json_metadata: JSON.stringify({ app: 'flexus' }),
    parentAuthor: getPreviewParentAuthor(),
    parentPermlink: selectedFeed || 'flex',
    depth: 1,
    votes: { total: 0, up: 0 },
    replies: 0,
  } : null;
  const charLimit = getFeedCharacterLimit(selectedFeed);
  const remainingChars = charLimit - countContentCharacters(previewContentRaw);
  const feedOptions = enabledFeedKeys.map((key) => ({ key, ...(FEED_DISPLAY_CONFIG[key] || {}) }));

  const modalContent = (
    <>
      <div className="modal-backdrop" onClick={handleBackdropClick}>
        <div className="create-post-modal">
          <div className="modal-header">
            <h2>Create Flex</h2>
            <button className="close-btn" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="post-form">
            <div className="form-group">
              <label className="form-label">Post to feed</label>
              <div className="feed-options">
                {feedOptions.map((feed) => (
                  <button
                    key={feed.key}
                    type="button"
                    onClick={() => setSelectedFeed(feed.key)}
                    className={`feed-option-btn ${selectedFeed === feed.key ? 'current-feed' : ''}`}
                  >
                    <span className="feed-icon">{feed.icon}</span>
                    <span className="feed-name">{feed.label}</span>
                    {selectedFeed === feed.key && <span className="current-badge">Selected</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <RichEditor
                ref={richEditorRef}
                value={bodyHtml}
                onChange={handleBodyChange}
                onSubmit={handleSubmit}
                placeholder="What's happening?"
                maxLength={null}
                toolbar={true}
                onUploadImage={handleUploadImage}
                outputFormat={outputFormat}
              />
              <div className="char-counter">
                <span className={remainingChars < 0 ? 'over-limit' : ''}>{remainingChars} characters remaining</span>
              </div>
            </div> {/* <-- DENNE </div> MANGLER I DIN KODE */}

            {isPoll && (
              <div className="poll-creator-box">
                <h4 className="poll-creator-title">Poll Setup</h4>
                <input
                  type="text"
                  className="poll-input-field"
                  placeholder="Ask a question..."
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                />

                <div className="poll-choices-container">
                  {pollChoices.map((choice, idx) => (
                    <div key={idx} className="poll-choice-row">
                      <input
                        type="text"
                        className="poll-input-field"
                        placeholder={`Choice ${idx + 1}`}
                        value={choice}
                        onChange={(e) => {
                          const updated = [...pollChoices];
                          updated[idx] = e.target.value;
                          setPollChoices(updated);
                        }}
                      />
                      {pollChoices.length > 2 && (
                        <button
                          type="button"
                          className="poll-remove-choice-btn"
                          title="Remove choice"
                          onClick={() => {
                            setPollChoices(pollChoices.filter((_, i) => i !== idx));
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}

                  {pollChoices.length < 10 && (
                    <button
                      type="button"
                      className="poll-add-choice-btn"
                      onClick={() => setPollChoices([...pollChoices, ''])}
                    >
                      + Add Option
                    </button>
                  )}
                </div>

                <div className="poll-duration-row">
                  <span>Poll duration:</span>
                  <input
                    type="number"
                    min="1"
                    max={pollDurationUnit === 'days' ? 7 : (pollDurationUnit === 'hours' ? 168 : 10080)}
                    value={pollDurationValue}
                    onChange={(e) => {
                      const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                      const max = pollDurationUnit === 'days' ? 7 : (pollDurationUnit === 'hours' ? 168 : 10080);
                      setPollDurationValue(Math.min(val, max));
                    }}
                    className="poll-duration-input"
                  />
                  <select
                    value={pollDurationUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      setPollDurationUnit(newUnit);
                      const max = newUnit === 'days' ? 7 : (newUnit === 'hours' ? 168 : 10080);
                      if (pollDurationValue > max) {
                        setPollDurationValue(max);
                      }
                    }}
                    className="poll-duration-select"
                  >
                    <option value="days">day(s)</option>
                    <option value="hours">hour(s)</option>
                    <option value="minutes">minute(s)</option>
                  </select>
                  <span className="poll-duration-hint">(max 7 days)</span>
                </div>
              </div>
            )}

            {postingError && <div className="error-message">{postingError}</div>}

            <div className="reply-actions">
              <div className="composer-meta-actions">
                <button
                  type="button"
                  className="preview-create-btn"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!previewContentRaw.trim() || posting}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className={`poll-toggle-btn ${isPoll ? 'active' : ''}`}
                  onClick={() => setIsPoll(!isPoll)}
                >
                  <span>📊</span>
                  <span>{isPoll ? 'Remove Poll' : 'Create Poll'}</span>
                </button>
              </div>
              <div className="posting-actions">
                <button
                  type="button"
                  className="reply-cancel-btn"
                  onClick={onClose}
                  disabled={posting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="reply-submit-btn"
                  disabled={posting || !previewContentRaw.trim() || remainingChars < 0}
                >
                  {posting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ComposerPreviewModal
        isOpen={previewOpen}
        title="Preview Flex"
        content={previewContent}
        previewNode={previewPost ? <PostCard post={previewPost} showActions={false} showCommentToggle={false} hideCommentButton={true} /> : null}
        author={user?.username || 'you'}
        focusTargetRef={richEditorRef}
        submitLabel="Post"
        isSubmitting={posting}
        onClose={() => setPreviewOpen(false)}
        onKeepWriting={() => setPreviewOpen(false)}
        onSubmit={async () => {
          setPreviewOpen(false);
          await handleSubmit();
        }}
      />
    </>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default CreatePostModal;